import React, { useState } from "react";
import toast from "react-hot-toast";
import { MealPlanData } from "types/mealPlans";

import MealPlanApiClient from "utils/mealPlanApiClient";

import InstacartCarrot from "images/Instacart_Carrot.png";

interface InstacartButtonProps {
  mealPlanData: MealPlanData;
  apiClient: MealPlanApiClient | null;
}

interface InstacartResponse {
  passio_redirect_url?: string;
  products_link_url?: string;
  tracked_products_link_url?: string;
  error?: string;
}

const InstacartButton: React.FC<InstacartButtonProps> = ({
  mealPlanData,
  apiClient,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // Check if affiliate links are allowed
  if (!apiClient || !apiClient.allowAffiliateLinks) {
    return null;
  }

  const handleInstacartClick = async () => {
    if (!apiClient || !mealPlanData) {
      toast.error("Unable to process request. Please try again.");
      return;
    }

    setIsLoading(true);

    try {
      // Send the full meal plan data - the backend will extract the shopping list
      const response = await apiClient.convertToAffiliateLink(mealPlanData);

      const instacartData = response.data as InstacartResponse;

      if (instacartData.error) {
        toast.error(`Error: ${instacartData.error}`);
        return;
      }

      const preferredLink =
        instacartData.passio_redirect_url ||
        instacartData.tracked_products_link_url ||
        instacartData.products_link_url;

      if (preferredLink) {
        // Open the Instacart link in a new tab
        window.open(preferredLink, "_blank");
        toast.success("Opening Instacart...");
      } else {
        toast.error("No shopping link returned. Please try again.");
      }
    } catch (error: any) {
      // console.error("Failed to get Instacart link:", error);
      toast.error("Failed to create shopping link. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleInstacartClick}
      disabled={isLoading}
      className="w-full mt-4 relative overflow-hidden group"
      style={{
        height: "46px",
        backgroundColor: "#003D29",
        borderRadius: "23px", // Half of height for fully rounded button
        padding: "0 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        border: "none",
        cursor: isLoading ? "not-allowed" : "pointer",
        transition: "background-color 0.2s ease",
        opacity: isLoading ? 0.7 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isLoading) {
          e.currentTarget.style.backgroundColor = "#002a1f";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "#003D29";
      }}
    >
      {/* Instacart Carrot Logo */}
      <img
        src={InstacartCarrot}
        alt="Instacart"
        style={{
          width: "22px",
          height: "22px",
          objectFit: "contain",
        }}
      />

      <span
        style={{
          color: "#FAF1E5",
          fontSize: "16px",
          fontWeight: 600,
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          letterSpacing: "-0.01em",
        }}
      >
        {isLoading ? "Processing..." : "Get Ingredients"}
      </span>
    </button>
  );
};

export default InstacartButton;
